/**
 * Price lists — `/catalog/price-lists`.
 *
 * Three things stacked, and the screen keeps them apart because the system
 * does. The LIST fixes a currency and a validity window. Its LINES are
 * quantity breaks: a variant, the minimum quantity that earns the price, the
 * price, and the dates that line is good for. Its BINDINGS say which scope the
 * list prices — everyone (base), one channel, or one customer group — and at
 * what priority when more than one could apply.
 *
 * Two things the screen refuses to do. It never parses a price into a number:
 * prices are exact decimal strings from the wire to the input and back, because
 * a price that has been through a float is not the price anybody agreed to.
 * And it never offers Delete on a list that is not a draft — an active list is
 * archived, since documents already reference the prices it set.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { formatCalendarDate } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  BINDING_TYPES,
  PRICE_LIST_STATUSES,
  createPriceList,
  createPriceListBinding,
  createPriceListLine,
  deletePriceList,
  deletePriceListBinding,
  deletePriceListLine,
  getPriceList,
  getPriceListBinding,
  getPriceListLine,
  listPriceListBindings,
  listPriceListLines,
  listPriceLists,
  updatePriceList,
  type PriceList,
  type PriceListBindingType,
  type PriceListStatus,
} from "../price-lists-api";

const ALL = "__all__";
const CCY = /^[A-Z]{3}$/;
/** NUMERIC(20,6) on the wire; the form keeps every digit it is given. */
const DECIMAL = /^\d{1,14}(\.\d{1,6})?$/;

export interface PriceListsPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  /** Variants, channels and customer groups are named through cross-domain
   *  reads; without the grant the screen shows the id it was given. */
  variantOptions?: { id: string; label: string }[];
  channelOptions?: { id: string; label: string }[];
  customerGroupOptions?: { id: string; label: string }[];
  defaultCurrency?: string;
}

export function PriceListsPage({
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  variantOptions = [],
  channelOptions = [],
  customerGroupOptions = [],
  defaultCurrency = "",
}: PriceListsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>(ALL);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    code: "",
    name: "",
    currency: defaultCurrency,
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: "",
  });
  const [newLine, setNewLine] = useState({
    variant_id: "",
    min_qty: "1.000000",
    unit_price: "",
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: "",
  });
  const [newBinding, setNewBinding] = useState<{
    binding_type: PriceListBindingType;
    binding_id: string;
    priority: string;
  }>({ binding_type: "base", binding_id: "", priority: "0" });

  const lists = useQuery({
    queryKey: ["catalog/price-lists", status],
    queryFn: async ({ signal }) => [
      ...(await listPriceLists(status === ALL ? {} : { status: status as PriceListStatus }, signal))
        .items,
    ],
  });

  const rows = lists.data ?? [];
  const selected: PriceList | undefined = rows.find((row) => row.id === selectedId) ?? rows[0];

  useEffect(() => {
    if (selected !== undefined && selectedId === null) setSelectedId(selected.id);
  }, [selected, selectedId]);

  const lines = useQuery({
    queryKey: ["catalog/price-lists", selected?.id, "lines"],
    queryFn: async ({ signal }) => [...(await listPriceListLines(selected!.id, signal)).items],
    enabled: selected !== undefined,
  });
  const bindings = useQuery({
    queryKey: ["catalog/price-lists", selected?.id, "bindings"],
    queryFn: async ({ signal }) => [...(await listPriceListBindings(selected!.id, signal)).items],
    enabled: selected !== undefined,
  });

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };
  const nameOf = (options: { id: string; label: string }[], id: string | undefined): string =>
    id === undefined ? "—" : (options.find((option) => option.id === id)?.label ?? id.slice(0, 8));

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["catalog/price-lists"] });
  };

  const run = async (work: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await refresh();
    } catch {
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const submitList = () =>
    void run(
      async () => {
        const created = await createPriceList(
          {
            code: draft.code.trim().toUpperCase(),
            name: draft.name.trim(),
            currency: draft.currency.trim().toUpperCase(),
            valid_from: draft.valid_from,
            ...(draft.valid_to === "" ? {} : { valid_to: draft.valid_to }),
          },
          newIdempotencyKey(),
        );
        setCreating(false);
        setSelectedId(created.id);
      },
      t("catalog.priceLists.saveError", "The price list could not be saved."),
    );

  const moveStatus = (next: PriceListStatus) => {
    if (selected === undefined) return;
    void run(
      async () => {
        const { etag } = await getPriceList(selected.id);
        if (etag === null) throw new Error("missing etag");
        await updatePriceList(selected.id, { status: next }, etag);
      },
      t("catalog.priceLists.saveError", "The price list could not be saved."),
    );
  };

  const removeList = () => {
    if (selected === undefined) return;
    void run(
      async () => {
        const { etag } = await getPriceList(selected.id);
        if (etag === null) throw new Error("missing etag");
        await deletePriceList(selected.id, etag);
        setSelectedId(null);
      },
      t("catalog.priceLists.deleteError", "The price list could not be deleted."),
    );
  };

  const addLine = () => {
    if (selected === undefined) return;
    void run(
      async () => {
        await createPriceListLine(
          selected.id,
          {
            variant_id: newLine.variant_id,
            min_qty: newLine.min_qty,
            unit_price: newLine.unit_price,
            effective_from: newLine.effective_from,
            ...(newLine.effective_to === "" ? {} : { effective_to: newLine.effective_to }),
          },
          newIdempotencyKey(),
        );
        setNewLine({ ...newLine, variant_id: "", unit_price: "" });
      },
      t("catalog.priceLists.lineError", "The line could not be saved."),
    );
  };

  const removeLine = (lineId: string) => {
    if (selected === undefined) return;
    void run(
      async () => {
        const { etag } = await getPriceListLine(selected.id, lineId);
        if (etag === null) throw new Error("missing etag");
        await deletePriceListLine(selected.id, lineId, etag);
      },
      t("catalog.priceLists.lineError", "The line could not be saved."),
    );
  };

  const addBinding = () => {
    if (selected === undefined) return;
    void run(
      async () => {
        await createPriceListBinding(
          selected.id,
          {
            binding_type: newBinding.binding_type,
            // `base` takes no target: it is the fallback, not a scope.
            ...(newBinding.binding_type === "base" ? {} : { binding_id: newBinding.binding_id }),
            priority: Number(newBinding.priority),
          },
          newIdempotencyKey(),
        );
        setNewBinding({ ...newBinding, binding_id: "" });
      },
      t("catalog.priceLists.bindingError", "The binding could not be saved."),
    );
  };

  const removeBinding = (bindingId: string) => {
    if (selected === undefined) return;
    void run(
      async () => {
        const { etag } = await getPriceListBinding(selected.id, bindingId);
        if (etag === null) throw new Error("missing etag");
        await deletePriceListBinding(selected.id, bindingId, etag);
      },
      t("catalog.priceLists.bindingError", "The binding could not be saved."),
    );
  };

  const listValid =
    draft.code.trim() !== "" &&
    draft.name.trim() !== "" &&
    CCY.test(draft.currency.trim().toUpperCase()) &&
    draft.valid_from !== "";
  const lineValid =
    newLine.variant_id !== "" &&
    DECIMAL.test(newLine.min_qty) &&
    DECIMAL.test(newLine.unit_price) &&
    newLine.effective_from !== "";
  const bindingValid = newBinding.binding_type === "base" || newBinding.binding_id !== "";

  const bindingTargets = (type: PriceListBindingType) =>
    type === "channel" ? channelOptions : type === "customer_group" ? customerGroupOptions : [];

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("catalog.priceLists.title", "Price lists")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "catalog.priceLists.subtitle",
              "What a variant sells for, to whom, in what quantity, and between which dates.",
            )}
          </p>
        </div>
        {canCreate ? (
          <Button type="button" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("catalog.priceLists.new", "New price list")}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="price-list-status">{t("catalog.priceLists.status", "Status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="price-list-status" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {PRICE_LIST_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`catalog.priceListStatus.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("catalog.priceLists.listTitle", "Price lists")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {lists.isLoading ? (
              <p className="p-2 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : rows.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">
                {t("catalog.priceLists.empty", "No price list matches this filter.")}
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      aria-current={row.id === selected?.id}
                      onClick={() => setSelectedId(row.id)}
                      className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm hover:bg-muted aria-[current=true]:bg-muted aria-[current=true]:font-medium"
                    >
                      <span className="min-w-0">
                        <span className="block truncate">{row.name}</span>
                        <span dir="ltr" className="block truncate font-mono text-xs opacity-70">
                          {row.code} · {row.currency}
                        </span>
                      </span>
                      <Badge
                        variant={
                          row.status === "active"
                            ? "success"
                            : row.status === "draft"
                              ? "outline"
                              : "secondary"
                        }
                      >
                        {t(`catalog.priceListStatus.${row.status}`, row.status)}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {selected === undefined ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {t("catalog.priceLists.pick", "Choose a price list to see its prices.")}
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2 className="flex flex-wrap items-baseline gap-2">
                    {selected.name}
                    <span dir="ltr" className="font-mono text-sm font-normal text-muted-foreground">
                      {selected.code}
                    </span>
                  </h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-end justify-between gap-3 text-sm">
                <p className="text-muted-foreground">
                  {t("catalog.priceLists.validity", "{{currency}} · from {{from}}{{to}}", {
                    currency: selected.currency,
                    from: day(selected.valid_from),
                    to:
                      selected.valid_to === undefined
                        ? ""
                        : ` ${t("catalog.priceLists.until", "until")} ${day(selected.valid_to)}`,
                  })}
                </p>
                {canUpdate ? (
                  <div className="flex flex-wrap gap-2">
                    {selected.status === "draft" ? (
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy}
                        onClick={() => moveStatus("active")}
                      >
                        {t("catalog.priceLists.activate", "Activate")}
                      </Button>
                    ) : null}
                    {selected.status === "active" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => moveStatus("archived")}
                      >
                        {t("catalog.priceLists.archive", "Archive")}
                      </Button>
                    ) : null}
                    {/* Only an unused draft deletes; anything that has priced a
                        document is archived instead. */}
                    {canDelete && selected.status === "draft" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={removeList}
                      >
                        {t("actions.delete", "Delete")}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("catalog.priceLists.linesTitle", "Quantity breaks")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {lines.isLoading ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
                ) : (lines.data ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("catalog.priceLists.noLines", "This list prices nothing yet.")}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[40rem] text-sm">
                      <thead>
                        <tr className="border-b text-xs uppercase text-muted-foreground">
                          <th scope="col" className="p-2 text-start">
                            {t("catalog.priceLists.variant", "Variant")}
                          </th>
                          <th scope="col" className="p-2 text-end">
                            {t("catalog.priceLists.minQty", "From qty")}
                          </th>
                          <th scope="col" className="p-2 text-end">
                            {t("catalog.priceLists.unitPrice", "Unit price")}
                          </th>
                          <th scope="col" className="p-2 text-start">
                            {t("catalog.priceLists.effective", "Effective")}
                          </th>
                          {canUpdate ? <th scope="col" className="p-2" /> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {(lines.data ?? []).map((line) => (
                          <tr key={line.id} className="border-b last:border-0">
                            <td className="p-2">{nameOf(variantOptions, line.variant_id)}</td>
                            <td className="p-2 text-end tabular-nums" dir="ltr">
                              {line.min_qty}
                            </td>
                            <td className="p-2 text-end tabular-nums" dir="ltr">
                              {line.unit_price}
                            </td>
                            <td className="p-2 text-muted-foreground">
                              {day(line.effective_from)}
                              {line.effective_to === undefined
                                ? ""
                                : ` – ${day(line.effective_to)}`}
                            </td>
                            {canUpdate ? (
                              <td className="p-2 text-end">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label={t("catalog.priceLists.removeLine", "Remove line")}
                                  disabled={busy}
                                  onClick={() => removeLine(line.id)}
                                >
                                  <Trash2 className="size-4" aria-hidden="true" />
                                </Button>
                              </td>
                            ) : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {canUpdate ? (
                  <div className="flex flex-wrap items-end gap-3 border-t pt-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="line-variant">
                        {t("catalog.priceLists.variant", "Variant")}
                      </Label>
                      <Select
                        value={newLine.variant_id}
                        onValueChange={(value) => setNewLine({ ...newLine, variant_id: value })}
                      >
                        <SelectTrigger id="line-variant" className="w-64">
                          <SelectValue placeholder={t("catalog.priceLists.pickVariant", "Pick…")} />
                        </SelectTrigger>
                        <SelectContent>
                          {variantOptions.map((option) => (
                            <SelectItem key={option.id} value={option.id}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="line-min-qty">
                        {t("catalog.priceLists.minQty", "From qty")}
                      </Label>
                      <Input
                        id="line-min-qty"
                        dir="ltr"
                        inputMode="decimal"
                        className="w-28 text-end font-mono"
                        value={newLine.min_qty}
                        onChange={(event) =>
                          setNewLine({ ...newLine, min_qty: event.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="line-price">
                        {t("catalog.priceLists.unitPrice", "Unit price")}
                      </Label>
                      <Input
                        id="line-price"
                        dir="ltr"
                        inputMode="decimal"
                        className="w-32 text-end font-mono"
                        value={newLine.unit_price}
                        onChange={(event) =>
                          setNewLine({ ...newLine, unit_price: event.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="line-from">{t("catalog.priceLists.from", "From")}</Label>
                      <Input
                        id="line-from"
                        type="date"
                        className="w-40"
                        value={newLine.effective_from}
                        onChange={(event) =>
                          setNewLine({ ...newLine, effective_from: event.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="line-to">{t("catalog.priceLists.to", "To")}</Label>
                      <Input
                        id="line-to"
                        type="date"
                        className="w-40"
                        value={newLine.effective_to}
                        onChange={(event) =>
                          setNewLine({ ...newLine, effective_to: event.target.value })
                        }
                      />
                    </div>
                    <Button type="button" disabled={!lineValid || busy} onClick={addLine}>
                      {t("catalog.priceLists.addLine", "Add break")}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("catalog.priceLists.bindingsTitle", "Who gets this list")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t(
                    "catalog.priceLists.bindingsHint",
                    "Base applies to everyone. A channel or customer-group binding wins over base, and the higher priority wins between two of the same kind.",
                  )}
                </p>
                {bindings.isLoading ? (
                  <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
                ) : (bindings.data ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("catalog.priceLists.noBindings", "Nothing resolves to this list yet.")}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {(bindings.data ?? []).map((binding) => (
                      <li
                        key={binding.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm"
                      >
                        <span className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">
                            {t(`catalog.bindingType.${binding.binding_type}`, binding.binding_type)}
                          </Badge>
                          <span>
                            {binding.binding_type === "base"
                              ? t("catalog.priceLists.everyone", "Everyone")
                              : nameOf(bindingTargets(binding.binding_type), binding.binding_id)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {t("catalog.priceLists.priority", "priority {{value}}", {
                              value: binding.priority,
                            })}
                          </span>
                        </span>
                        {canUpdate ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("catalog.priceLists.removeBinding", "Remove binding")}
                            disabled={busy}
                            onClick={() => removeBinding(binding.id)}
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}

                {canUpdate ? (
                  <div className="flex flex-wrap items-end gap-3 border-t pt-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="binding-type">
                        {t("catalog.priceLists.bindingType", "Applies to")}
                      </Label>
                      <Select
                        value={newBinding.binding_type}
                        onValueChange={(value) =>
                          setNewBinding({
                            ...newBinding,
                            binding_type: value as PriceListBindingType,
                            binding_id: "",
                          })
                        }
                      >
                        <SelectTrigger id="binding-type" className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BINDING_TYPES.map((value) => (
                            <SelectItem key={value} value={value}>
                              {t(`catalog.bindingType.${value}`, value)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {newBinding.binding_type === "base" ? null : (
                      <div className="space-y-1.5">
                        <Label htmlFor="binding-target">
                          {t("catalog.priceLists.bindingTarget", "Which one")}
                        </Label>
                        <Select
                          value={newBinding.binding_id}
                          onValueChange={(value) =>
                            setNewBinding({ ...newBinding, binding_id: value })
                          }
                        >
                          <SelectTrigger id="binding-target" className="w-56">
                            <SelectValue
                              placeholder={t("catalog.priceLists.pickTarget", "Pick…")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {bindingTargets(newBinding.binding_type).map((option) => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label htmlFor="binding-priority">
                        {t("catalog.priceLists.priorityLabel", "Priority")}
                      </Label>
                      <Input
                        id="binding-priority"
                        dir="ltr"
                        inputMode="numeric"
                        className="w-24 text-end font-mono"
                        value={newBinding.priority}
                        onChange={(event) =>
                          setNewBinding({ ...newBinding, priority: event.target.value })
                        }
                      />
                    </div>
                    <Button type="button" disabled={!bindingValid || busy} onClick={addBinding}>
                      {t("catalog.priceLists.addBinding", "Add binding")}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("catalog.priceLists.new", "New price list")}</DialogTitle>
            <DialogDescription>
              {t(
                "catalog.priceLists.formHint",
                "A list is created as a draft: it prices nothing until it is activated.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pl-code">{t("catalog.priceLists.code", "Code")}</Label>
              <Input
                id="pl-code"
                dir="ltr"
                className="font-mono uppercase"
                value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pl-name">{t("catalog.priceLists.name", "Name")}</Label>
              <Input
                id="pl-name"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pl-currency">{t("catalog.priceLists.currency", "Currency")}</Label>
              <Input
                id="pl-currency"
                dir="ltr"
                maxLength={3}
                className="w-24 font-mono uppercase"
                value={draft.currency}
                onChange={(event) => setDraft({ ...draft, currency: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pl-from">{t("catalog.priceLists.from", "From")}</Label>
              <Input
                id="pl-from"
                type="date"
                value={draft.valid_from}
                onChange={(event) => setDraft({ ...draft, valid_from: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pl-to">{t("catalog.priceLists.to", "To")}</Label>
              <Input
                id="pl-to"
                type="date"
                value={draft.valid_to}
                onChange={(event) => setDraft({ ...draft, valid_to: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreating(false)}>
              {t("actions.cancel", "Cancel")}
            </Button>
            <Button type="button" disabled={!listValid || busy} onClick={submitList}>
              {t("actions.save", "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
